import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, Filter, RefreshCw, ChevronDown, ChevronUp, 
  Mail, Phone, User, Calendar, DollarSign, CheckCircle,
  XCircle, Clock, AlertCircle, Package, TrendingUp
} from 'lucide-react';
import { apiService } from '../config/api';

const ManageCatlogInvoices = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    status: 'all',
    invoiceId: 'all',
    search: ''
  });
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    completed: 0,
    failed: 0,
    totalPaid: 0
  });

  const ADMIN_PASSWORD = 'VISApath123$';

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      fetchInvoices();
    } else {
      alert('Incorrect password');
    }
  };

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const options = {
        status: filters.status !== 'all' ? filters.status : undefined,
        invoiceId: filters.invoiceId !== 'all' ? filters.invoiceId : undefined,
        search: filters.search || undefined,
        limit: 100
      };

      const response = await apiService.getCatalogPaymentRequests(options);
      
      if (response.success) {
        setInvoices(response.data.requests);
        calculateStats(response.data.requests);
      }
    } catch (error) {
      console.error('Error fetching invoices:', error);
      alert('Failed to fetch invoices');
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (data) => {
    const stats = {
      total: data.length,
      pending: data.filter(inv => inv.status === 'pending').length,
      completed: data.filter(inv => inv.status === 'completed').length,
      failed: data.filter(inv => inv.status === 'failed').length,
      totalPaid: data.reduce((sum, inv) => sum + (inv.totalPaidInvoices || 0), 0)
    };
    setStats(stats);
  };

  const toggleRow = (id) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedRows(newExpanded);
  };

  const handleMarkAsPaid = async (invoice) => {
    if (!invoice.currentInvoiceId) {
      alert('No current invoice to mark as paid');
      return;
    }

    const confirmed = window.confirm(
      `Mark Invoice #${invoice.currentInvoiceId} as paid?\n\n` +
      `Customer: ${invoice.fullName}\n` +
      `Email: ${invoice.originalEmail}\n` +
      `Product: ${invoice.currentProductDescription}\n` +
      `Amount: $${invoice.currentProductPrice}\n\n` +
      `This will send a receipt email to the customer.`
    );

    if (!confirmed) return;

    try {
      const response = await apiService.markCatalogInvoiceAsPaid(invoice._id);
      
      if (response.success) {
        alert(`Invoice marked as paid! Receipt sent to ${invoice.originalEmail}`);
        fetchInvoices();
      }
    } catch (error) {
      console.error('Error marking as paid:', error);
      alert('Failed to mark invoice as paid: ' + error.message);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchInvoices();
    }
  }, [filters]);

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full"
        >
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <Package className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Catalog Admin</h2>
            <p className="text-gray-600 mt-2">Enter password to access invoice management</p>
          </div>
          
          <form onSubmit={handleLogin}>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter admin password"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-4"
              autoFocus
            />
            <button
              type="submit"
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg transition-colors"
            >
              Access Dashboard
            </button>
          </form>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Catalog Invoice Management</h1>
          <p className="text-gray-600">Manage and track all catalog payment requests</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Customers</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              </div>
              <User className="w-8 h-8 text-blue-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Paid Invoices</p>
                <p className="text-2xl font-bold text-green-600">{stats.totalPaid}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Pending</p>
                <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
              </div>
              <Clock className="w-8 h-8 text-yellow-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Completed</p>
                <p className="text-2xl font-bold text-blue-600">{stats.completed}</p>
              </div>
              <TrendingUp className="w-8 h-8 text-blue-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Failed</p>
                <p className="text-2xl font-bold text-red-600">{stats.failed}</p>
              </div>
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search by name or email..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="processing">Processing</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
            </select>

            <select
              value={filters.invoiceId}
              onChange={(e) => setFilters({ ...filters, invoiceId: e.target.value })}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Invoices</option>
              <option value="1">Invoice #1</option>
              <option value="2">Invoice #2</option>
              <option value="3">Invoice #3</option>
              <option value="4">Invoice #4</option>
            </select>

            <button
              onClick={fetchInvoices}
              disabled={loading}
              className="flex items-center justify-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:bg-gray-400"
            >
              <RefreshCw className={`w-5 h-5 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Invoices Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Current Invoice
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Stats
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Details
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {invoices.map((invoice) => (
                  <React.Fragment key={invoice._id}>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex items-start">
                          <User className="w-5 h-5 text-gray-400 mr-2 mt-0.5" />
                          <div>
                            <p className="font-medium text-gray-900">{invoice.fullName}</p>
                            <p className="text-sm text-gray-600 flex items-center mt-1">
                              <Mail className="w-4 h-4 mr-1" />
                              {invoice.originalEmail || invoice.email}
                            </p>
                            {invoice.originalEmail && invoice.email !== invoice.originalEmail && (
                              <p className="text-xs text-gray-500 ml-5">Used: {invoice.email}</p>
                            )}
                            <p className="text-sm text-gray-600 flex items-center mt-1">
                              <Phone className="w-4 h-4 mr-1" />
                              {invoice.phone}
                            </p>
                          </div>
                        </div>
                      </td>

                      <td className="px-6 py-4">
                        {invoice.currentInvoiceId ? (
                          <div>
                            <p className="font-medium text-gray-900">
                              Invoice #{invoice.currentInvoiceId}
                            </p>
                            <p className="text-sm text-gray-600">
                              {invoice.currentProductDescription || invoice.currentProductName}
                            </p>
                            <p className="text-sm font-semibold text-blue-600">
                              ${invoice.currentProductPrice}
                            </p>
                          </div>
                        ) : (
                          <span className="text-gray-400">No current invoice</span>
                        )}
                      </td>

                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          invoice.status === 'completed' ? 'bg-green-100 text-green-800' :
                          invoice.status === 'processing' ? 'bg-blue-100 text-blue-800' :
                          invoice.status === 'failed' ? 'bg-red-100 text-red-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {invoice.status}
                        </span>
                      </td>

                      <td className="px-6 py-4">
                        <div className="text-sm">
                          <p className="text-gray-600">
                            <span className="font-medium">{invoice.totalAttempts || 0}</span> attempts
                          </p>
                          <p className="text-green-600">
                            <span className="font-medium">{invoice.totalPaidInvoices || 0}</span> paid
                          </p>
                        </div>
                      </td>

                      <td className="px-6 py-4">
                        {invoice.currentInvoiceId && (
                          <button
                            onClick={() => handleMarkAsPaid(invoice)}
                            className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-sm rounded-lg transition-colors"
                          >
                            Mark as Paid
                          </button>
                        )}
                      </td>

                      <td className="px-6 py-4">
                        <button
                          onClick={() => toggleRow(invoice._id)}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          {expandedRows.has(invoice._id) ? (
                            <ChevronUp className="w-5 h-5" />
                          ) : (
                            <ChevronDown className="w-5 h-5" />
                          )}
                        </button>
                      </td>
                    </tr>

                    <AnimatePresence>
                      {expandedRows.has(invoice._id) && (
                        <motion.tr
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                        >
                          <td colSpan="6" className="px-6 py-4 bg-gray-50">
                            <div className="space-y-4">
                              {/* Paid Invoices */}
                              {invoice.paidInvoices && invoice.paidInvoices.length > 0 && (
                                <div>
                                  <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                                    <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                                    Paid Invoices ({invoice.paidInvoices.length})
                                  </h4>
                                  <div className="space-y-2">
                                    {invoice.paidInvoices.map((paid, idx) => (
                                      <div key={idx} className="bg-white p-3 rounded border border-green-200">
                                        <div className="flex justify-between items-start">
                                          <div>
                                            <p className="font-medium text-gray-900">
                                              Invoice #{paid.invoiceId} - {paid.productDescription}
                                            </p>
                                            <p className="text-sm text-gray-600">
                                              Paid: {new Date(paid.paidAt).toLocaleDateString()}
                                            </p>
                                            {paid.receiptSentAt && (
                                              <p className="text-xs text-green-600">
                                                Receipt sent: {new Date(paid.receiptSentAt).toLocaleString()}
                                              </p>
                                            )}
                                          </div>
                                          <p className="font-semibold text-green-600">
                                            ${paid.productPrice}
                                          </p>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Attempt History */}
                              {invoice.attemptHistory && invoice.attemptHistory.length > 0 && (
                                <div>
                                  <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                                    <Clock className="w-5 h-5 text-blue-600 mr-2" />
                                    Attempt History ({invoice.attemptHistory.length})
                                  </h4>
                                  <div className="space-y-1">
                                    {invoice.attemptHistory.slice().reverse().map((attempt, idx) => (
                                      <div key={idx} className="flex items-center text-sm">
                                        <span className={`w-2 h-2 rounded-full mr-2 ${
                                          attempt.status === 'success' ? 'bg-green-500' :
                                          attempt.status === 'failed' ? 'bg-red-500' :
                                          'bg-yellow-500'
                                        }`}></span>
                                        <span className="text-gray-600">
                                          Invoice #{attempt.invoiceId} - {new Date(attempt.attemptedAt).toLocaleString()} - 
                                          <span className={`ml-1 font-medium ${
                                            attempt.status === 'success' ? 'text-green-600' :
                                            attempt.status === 'failed' ? 'text-red-600' :
                                            'text-yellow-600'
                                          }`}>
                                            {attempt.status}
                                          </span>
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </td>
                        </motion.tr>
                      )}
                    </AnimatePresence>
                  </React.Fragment>
                ))}
              </tbody>
            </table>

            {invoices.length === 0 && !loading && (
              <div className="text-center py-12">
                <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No invoices found</p>
              </div>
            )}

            {loading && (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-4 text-gray-600">Loading invoices...</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageCatlogInvoices;
